import { useCallback, useEffect, useState } from 'react';

import { useTranslation } from '@kc-console/core';

import { INITIAL_FALLBACK_ICON_DATA_URL } from '../constants';
import useSpriteIcon from './useSpriteIcon';

import type { DataUrl } from '../types';

/**
 * 아이콘을 불러올 수 없을 때 임시 아이콘으로 대체하는 훅
 * @param placeholder - 아이콘 소스 URL
 * @returns { handleError: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void }
 * @example
 * // const { handleError } = useFallbackIcon('/icons/sprite.svg');
 * // handleError({ currentTarget: { src: '/icons/sprite.svg' } } as React.SyntheticEvent<HTMLImageElement, Event>);
 */
const useFallbackIcon = (placeholder: string) => {
  const { t } = useTranslation();
  const [fallbackIconDataUrl, setFallbackIconDataUrl] = useState<DataUrl>(
    INITIAL_FALLBACK_ICON_DATA_URL,
  );
  const { getSpriteIconDataUrl } = useSpriteIcon();

  useEffect(() => {
    const loadFallbackIcon = async () => {
      const fallbackIconDataUrl = await getSpriteIconDataUrl(placeholder);
      setFallbackIconDataUrl(fallbackIconDataUrl);
    };
    loadFallbackIcon();
  }, [getSpriteIconDataUrl, placeholder]);

  const handleError = useCallback(
    ({ currentTarget }: React.SyntheticEvent<HTMLImageElement, Event>) => {
      // currentTarget의 src는 이미 오류가 발생한 소스 URL이므로 임시 아이콘(어느 상황에도 유효한 값) 으로 대체합니다.
      currentTarget.src = fallbackIconDataUrl;
      console.warn(t('ui:icon.warning.fallback_icon'));
    },
    [fallbackIconDataUrl],
  );

  return {
    handleError,
  };
};

export default useFallbackIcon;
